/**
 * Platform Admin → Plans: one card per plan, grouped into Accounts, Scanning, Data and PCI readiness.
 * Editable settings use "Unlimited" switches instead of -1; limits the platform enforces are shown read-only.
 */

import { useState } from 'react';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useTiers, useUpdateTier } from '@/hooks/useTiers';
import type { TierConfig, TierConfigUpdateRequest } from '@/types/tier';
import { apiError } from '@/utils/apiError';
import { PLAN_NAMES, PLAN_ORDER, connections, count, effectiveMonthly, hours } from '@/pages/platform/planFormat';

const PLATFORM_NOTE = 'Set by the platform and stated in the Acceptable Use Policy';
const CADENCES = ['monthly', 'quarterly', 'yearly'] as const;

function Row({ label, value, platform }: { label: string; value: string; platform?: boolean }) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-right font-medium text-gray-900">{value}{platform && <span title={PLATFORM_NOTE} className="ml-0.5 text-indigo-600">*</span>}</dd>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return <div><h4 className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</h4><dl className="divide-y divide-gray-100">{children}</dl></div>;
}

/** A whole-number limit with an "Unlimited" (or "use the platform ceiling") switch; -1 means unlimited. */
function LimitField({ label, value, onChange, switchLabel, fallback, error }: {
  label: string; value: number; onChange: (value: number) => void; switchLabel: string; fallback: number; error?: string;
}) {
  const unlimited = value === -1;
  const id = `${label}-${switchLabel}`.replace(/\W+/g, '-');
  return (
    <div className="text-sm">
      <label htmlFor={id} className="block font-medium text-gray-700">{label}</label>
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
        <input id={id} type="number" min={0} className="input-field w-32" disabled={unlimited} value={unlimited ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))} />
        <label className="flex items-center gap-1.5 whitespace-nowrap text-gray-600">
          <input type="checkbox" checked={unlimited} aria-label={`${label}: ${switchLabel}`} onChange={(e) => onChange(e.target.checked ? -1 : fallback)} />
          {switchLabel.charAt(0).toUpperCase() + switchLabel.slice(1)}
        </label>
      </div>
      {error && <p className="mt-1 text-red-700">{error}</p>}
    </div>
  );
}

function wholeOrUnlimited(value: number) {
  return Number.isSafeInteger(value) && value >= -1;
}

function PlanEditor({ plan, onDone }: { plan: TierConfig; onDone: () => void }) {
  const update = useUpdateTier();
  const ceiling = plan.platform?.monthly_ceiling ?? -1;
  const [form, setForm] = useState({
    max_users: plan.max_users, max_assets: plan.max_assets, max_credentials: plan.max_credentials, max_agents: plan.max_agents,
    max_scans_per_month: plan.max_scans_per_month, report_retention_days: plan.report_retention_days,
    pci_enabled: !!plan.pci_enabled, pci_scan_limit: plan.pci_scan_limit ?? -1, pci_scan_period: plan.pci_scan_period ?? 'monthly',
    pci_scheduling_enabled: !!plan.pci_scheduling_enabled,
    pci_schedule_cadences: (plan.pci_schedule_cadences ?? 'monthly,quarterly,yearly').split(',').filter(Boolean),
    ai_connections_enabled: !!plan.ai_connections_enabled,
  });
  const set = (key: keyof typeof form) => (value: unknown) => setForm((current) => ({ ...current, [key]: value }));
  const paid = plan.tier !== 'free';
  const scansError = ceiling !== -1 && form.max_scans_per_month > ceiling ? `Can't exceed the platform ceiling of ${count(ceiling)}.` : undefined;
  const numbers = [form.max_users, form.max_assets, form.max_credentials, form.max_agents, form.max_scans_per_month, form.report_retention_days, form.pci_scan_limit];
  const invalid = !!scansError || !numbers.every(wholeOrUnlimited) || (paid && form.pci_scheduling_enabled && !form.pci_schedule_cadences.length);

  function save(event: React.FormEvent) {
    event.preventDefault();
    const payload: TierConfigUpdateRequest = {
      max_users: form.max_users, max_assets: form.max_assets, max_credentials: form.max_credentials, max_agents: form.max_agents,
      max_scans_per_month: form.max_scans_per_month, report_retention_days: form.report_retention_days,
      pci_enabled: paid && form.pci_enabled, pci_scan_limit: form.pci_scan_limit, pci_scan_period: form.pci_scan_period,
      pci_scheduling_enabled: paid && form.pci_scheduling_enabled, pci_schedule_cadences: form.pci_schedule_cadences.join(','),
      ai_connections_enabled: form.ai_connections_enabled,
    };
    update.mutate({ tier: plan.tier, payload }, { onSuccess: onDone });
  }

  return (
    <form onSubmit={save} className="space-y-4">
      {update.error && <p role="alert" className="rounded bg-red-50 p-2 text-sm text-red-700">{apiError(update.error)}</p>}
      <fieldset className="space-y-3"><legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Accounts</legend>
        <LimitField label="Users per organization" value={form.max_users} onChange={set('max_users')} switchLabel="unlimited" fallback={plan.max_users === -1 ? 10 : plan.max_users} />
        <LimitField label="Assets per organization" value={form.max_assets} onChange={set('max_assets')} switchLabel="unlimited" fallback={plan.max_assets === -1 ? 100 : plan.max_assets} />
        <LimitField label="Credentials" value={form.max_credentials} onChange={set('max_credentials')} switchLabel="unlimited" fallback={plan.max_credentials === -1 ? 10 : plan.max_credentials} />
        <LimitField label="Agents" value={form.max_agents} onChange={set('max_agents')} switchLabel="unlimited" fallback={plan.max_agents === -1 ? 1 : plan.max_agents} />
      </fieldset>
      <fieldset className="space-y-3"><legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Scanning and data</legend>
        <LimitField label="Scans per month" value={form.max_scans_per_month} onChange={set('max_scans_per_month')}
          switchLabel={ceiling === -1 ? 'unlimited' : 'use the platform ceiling'} fallback={ceiling === -1 ? 100 : ceiling} error={scansError} />
        <LimitField label="Retention (days)" value={form.report_retention_days} onChange={set('report_retention_days')} switchLabel="keep forever" fallback={plan.report_retention_days === -1 ? 365 : plan.report_retention_days} />
      </fieldset>
      <fieldset className="space-y-2 text-sm"><legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Integrations</legend>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.ai_connections_enabled} onChange={(e) => set('ai_connections_enabled')(e.target.checked)} />AI connections (MCP)</label>
      </fieldset>
      {paid && (
        <fieldset className="space-y-2 text-sm"><legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">PCI readiness</legend>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.pci_enabled} onChange={(e) => set('pci_enabled')(e.target.checked)} />PCI readiness enabled</label>
          {form.pci_enabled && <>
            <LimitField label="PCI scans per period" value={form.pci_scan_limit} onChange={set('pci_scan_limit')} switchLabel="unlimited" fallback={10} />
            <label className="block font-medium text-gray-700">PCI allowance period
              <select className="input-field mt-1 w-40" value={form.pci_scan_period} onChange={(e) => set('pci_scan_period')(e.target.value)}>
                {CADENCES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.pci_scheduling_enabled} onChange={(e) => set('pci_scheduling_enabled')(e.target.checked)} />Automatic PCI scheduling</label>
            {form.pci_scheduling_enabled && <div className="flex gap-3">{CADENCES.map((c) => (
              <label key={c} className="flex items-center gap-1"><input type="checkbox" checked={form.pci_schedule_cadences.includes(c)}
                onChange={(e) => set('pci_schedule_cadences')(e.target.checked ? [...form.pci_schedule_cadences, c] : form.pci_schedule_cadences.filter((x) => x !== c))} />{c}</label>
            ))}</div>}
          </>}
        </fieldset>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onDone}>Cancel</button>
        <button type="submit" className="btn-primary" disabled={invalid || update.isPending}>{update.isPending ? 'Saving…' : 'Save'}</button>
      </div>
    </form>
  );
}

function PlanCard({ plan }: { plan: TierConfig }) {
  const [editing, setEditing] = useState(false);
  const name = PLAN_NAMES[plan.tier] ?? plan.tier;
  const p = plan.platform;
  return (
    <section aria-label={`${name} plan`} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{name}</h3>
        {!editing && <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>Edit</button>}
      </div>
      {editing ? <div className="mt-3"><PlanEditor plan={plan} onDone={() => setEditing(false)} /></div> : (
        <>
          <Group title="Accounts">
            <Row label="Users per organization" value={count(plan.max_users)} />
            <Row label="Assets per organization" value={count(plan.max_assets)} />
            <Row label="Credentials" value={count(plan.max_credentials)} />
            <Row label="Agents" value={count(plan.max_agents)} />
            <Row label="Agent connections" value={connections(plan.allowed_connection_types)} />
          </Group>
          <Group title="Scanning">
            <Row label="Scans per month" value={count(effectiveMonthly(plan))} />
            {p && <>
              <Row label="Concurrent scans" value={count(p.concurrent_scans)} platform />
              <Row label="Gap between scans of the same target" value={hours(p.gap_hours)} platform />
              <Row label="Targets per scan" value={count(p.targets_per_scan)} platform />
            </>}
          </Group>
          <Group title="Data">
            <Row label="Retention" value={plan.report_retention_days === -1 ? 'Kept until deleted' : `${count(plan.report_retention_days)} days`} />
          </Group>
          <Group title="Integrations">
            <Row label="AI connections (MCP)" value={plan.ai_connections_enabled ? 'Included' : 'Not included'} />
          </Group>
          <Group title="PCI readiness">
            <Row label="PCI readiness" value={plan.tier === 'free' ? 'Not available' : plan.pci_enabled ? `Enabled, ${count(plan.pci_scan_limit ?? -1)} scans ${plan.pci_scan_period ?? 'monthly'}` : 'Disabled'} />
            {plan.tier !== 'free' && plan.pci_enabled && <Row label="Automatic scheduling" value={plan.pci_scheduling_enabled ? (plan.pci_schedule_cadences ?? '').split(',').join(', ') : 'Off'} />}
          </Group>
          {p && <p className="mt-3 text-xs text-gray-500"><span className="text-indigo-600">*</span> {PLATFORM_NOTE}. Scans per month never exceed the platform ceiling of {count(p.monthly_ceiling)}; every plan has a gap of at least {hours(p.minimum_gap_hours)}.</p>}
        </>
      )}
    </section>
  );
}

export default function PlatformPlansPage() {
  const { data: tiers = [], isLoading, error } = useTiers();
  if (isLoading) return <LoadingSpinner label="Loading plans..." />;
  const plans = [...tiers].sort((a, b) => PLAN_ORDER.indexOf(a.tier) - PLAN_ORDER.indexOf(b.tier));
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Plans</h1>
        <p className="mt-1 text-sm text-gray-600">The limits each subscription plan gives an organization. Changes apply to every organization on the plan.</p>
      </div>
      {error && <p role="alert" className="rounded bg-red-50 p-3 text-red-700">{apiError(error)}</p>}
      <div className="grid gap-5 xl:grid-cols-2">{plans.map((plan) => <PlanCard key={plan.tier} plan={plan} />)}</div>
    </div>
  );
}
